import { useEffect, useState } from "react";
import Container from "@/components/Container";
import Pager, { usePager } from "@/components/Pager";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, X } from "lucide-react";

const CATEGORIES = [
  { v: "kitchen",    l: "Kitchen" },
  { v: "bathroom",   l: "Bathroom" },
  { v: "structural", l: "Structural" },
  { v: "systems",    l: "Systems (elec/heat/plumb)" },
  { v: "exterior",   l: "Exterior" },
  { v: "finishes",   l: "Finishes" },
  { v: "general",    l: "General / misc" },
];

const UNITS = ["each", "room", "bedroom", "m2", "lm", "day"];

const fmt = (n) => "£" + (Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });

export default function RefurbLibrary() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", category: "kitchen", unit_cost: "", unit: "each", typical_qty: 1, notes: "" });
  const [editing, setEditing] = useState({}); // id -> draft

  const load = () => api.get("/refurb-items").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.unit_cost) return toast.error("Name + unit cost required");
    try {
      await api.post("/refurb-items", { ...form, unit_cost: Number(form.unit_cost), typical_qty: Number(form.typical_qty) || 1 });
      toast.success("Item added");
      setShowForm(false);
      setForm({ name: "", category: "kitchen", unit_cost: "", unit: "each", typical_qty: 1, notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const saveRow = async (id) => {
    const draft = editing[id]; if (!draft) return;
    try {
      await api.put(`/refurb-items/${id}`, { ...draft, unit_cost: Number(draft.unit_cost) || 0 });
      toast.success("Saved");
      setEditing({ ...editing, [id]: undefined });
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this refurb item?")) return;
    await api.delete(`/refurb-items/${id}`);
    toast.success("Deleted"); load();
  };

  const filtered = filter === "all" ? items : items.filter(i => i.category === filter);
  const grandTotal = filtered.reduce((s, i) => s + (Number(i.unit_cost) || 0), 0);
  const pager = usePager(filtered, 12);

  return (
    <Container className="py-8 md:py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Library</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Refurb Cost Library</h1>
          <p className="text-ivory/50 mt-2 text-sm">Your unit-cost price book. Tick items on any property to auto-build a renovation estimate.</p>
        </div>
        <button onClick={() => setShowForm(s => !s)} className="btn-ruby" data-testid="refurb-add-toggle">
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancel" : "New Item"}
        </button>
      </div>

      {/* Filter tabs */}
      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => setFilter("all")} data-testid="cat-all"
          className={`px-3 py-1.5 text-[0.7rem] uppercase tracking-[0.18em] border ${filter==="all" ? "border-ruby text-ruby" : "border-charcoal-600 text-ivory/60"}`}>
          All <span className="text-ivory/40">{items.length}</span>
        </button>
        {CATEGORIES.map(c => {
          const count = items.filter(i => i.category === c.v).length;
          return (
            <button key={c.v} onClick={() => setFilter(c.v)} data-testid={`cat-${c.v}`}
              className={`px-3 py-1.5 text-[0.7rem] uppercase tracking-[0.18em] border ${filter===c.v ? "border-ruby text-ruby" : "border-charcoal-600 text-ivory/60 hover:text-ivory"}`}>
              {c.l} <span className="text-ivory/40">{count}</span>
            </button>
          );
        })}
      </div>

      {showForm && (
        <form onSubmit={add} className="luxury-card-ruby p-6 mt-6 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="refurb-form">
          <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2" placeholder="Name (e.g. New roof)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
          <select className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="form-category">
            {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
          <input type="number" className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Unit cost £" value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: e.target.value })} data-testid="form-unit-cost" />
          <select className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} data-testid="form-unit">
            {UNITS.map(u => <option key={u}>{u}</option>)}
          </select>
          <button className="btn-ruby justify-center" data-testid="form-submit"><Save size={13}/> Save</button>
        </form>
      )}

      <div className="mt-6 luxury-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="refurb-table">
          <thead className="bg-charcoal-700 text-ivory/60 text-[0.65rem] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left p-3">Item</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Unit cost</th>
              <th className="text-left p-3">Unit</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-ivory/40">No items in this category yet.</td></tr>}
            {pager.pageItems.map(it => {
              const d = editing[it.id];
              return (
                <tr key={it.id} className="border-t border-charcoal-600/60 hover:bg-charcoal-800/40" data-testid={`refurb-row-${it.id}`}>
                  <td className="p-2">
                    {d ? (
                      <input className="bg-charcoal-800 border border-charcoal-600 px-2 py-1 text-sm w-full" value={d.name} onChange={e => setEditing({ ...editing, [it.id]: { ...d, name: e.target.value } })} />
                    ) : <span className="text-ivory">{it.name}</span>}
                  </td>
                  <td className="p-2">
                    {d ? (
                      <select className="bg-charcoal-800 border border-charcoal-600 px-2 py-1 text-sm" value={d.category} onChange={e => setEditing({ ...editing, [it.id]: { ...d, category: e.target.value } })}>
                        {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                      </select>
                    ) : <span className="text-[0.65rem] uppercase tracking-[0.15em] text-gold">{it.category}</span>}
                  </td>
                  <td className="p-2 text-right">
                    {d ? (
                      <input type="number" className="bg-charcoal-800 border border-charcoal-600 px-2 py-1 text-sm w-32 text-right" value={d.unit_cost} onChange={e => setEditing({ ...editing, [it.id]: { ...d, unit_cost: e.target.value } })} />
                    ) : <span className="text-ivory">{fmt(it.unit_cost)}</span>}
                  </td>
                  <td className="p-2">
                    {d ? (
                      <select className="bg-charcoal-800 border border-charcoal-600 px-2 py-1 text-sm" value={d.unit} onChange={e => setEditing({ ...editing, [it.id]: { ...d, unit: e.target.value } })}>
                        {UNITS.map(u => <option key={u}>{u}</option>)}
                      </select>
                    ) : <span className="text-ivory/60">{it.unit}</span>}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">
                    {d ? (
                      <>
                        <button onClick={() => saveRow(it.id)} className="btn-outline !py-1 !px-3 !text-[0.65rem] mr-1" data-testid={`save-${it.id}`}><Save size={11}/> Save</button>
                        <button onClick={() => setEditing({ ...editing, [it.id]: undefined })} className="btn-outline !py-1 !px-3 !text-[0.65rem]">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditing({ ...editing, [it.id]: { ...it } })} className="btn-outline !py-1 !px-3 !text-[0.65rem] mr-1" data-testid={`edit-${it.id}`}>Edit</button>
                        <button onClick={() => del(it.id)} className="btn-outline !py-1 !px-2 !text-[0.65rem] !border-ruby/40 !text-ruby-light" data-testid={`del-${it.id}`}><Trash2 size={11}/></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager {...pager} testid="refurb-pager" />

      <p className="mt-3 text-xs text-ivory/40">
        {filtered.length} items shown · combined value <span className="text-gold">{fmt(grandTotal)}</span>
      </p>
    </Container>
  );
}
